import { Injectable } from '@angular/core';
import { HttpWrapper } from '../../shared/services';
import { from, Observable } from 'rxjs';
import { GeoCoordinate } from '../models/GeoCoordinate';
import _ from 'lodash';
import moment from 'moment';
import { DateRange } from '../../shared/components/date-range.component';
import { KeycloakService } from '../../authentication/keycloak.service';
import { ActionType, DataBaseAction } from 'src/app/shared/services/database-action.service';
import { VesselServiceSpa } from 'src/app/generated/services/transportation/vessels/vesselSpa';
import { Vessel, VesselResponse, VesselType } from 'src/app/generated/services/transportation/vessels/vessel';
export { Vessel, Certificate, CertificateInfo, VesselType } from 'src/app/generated/services/transportation/vessels/vessel';
import { Voyage } from 'src/app/generated/services/transportation/voyages/voyage';
import { VoyageServiceSpa } from 'src/app/generated/services/transportation/voyages/voyageSpa';
import { Versioned } from 'src/app/shared/helpers/versioned';
export { Voyage, Discharge, BillOfLading, ProviderMeasurement } from 'src/app/generated/services/transportation/voyages/voyage';

export interface Document {
  documentType: string;
  documentId: string;
  issueDate: number;
  isDeleted?: boolean;
}

export interface STS {
  vesselImo: string;
  vesselName: string;
  shipVoyageNo: string;
}

export interface ToolbarItem {
  dateRange: DateRange;
  type: string;
  firstSOF: string;
  secondeSOF: string;
}

export interface DurationSof {
  vesselImo: string;
  vesselName: string;
  shipVoyageNo: string;
  startTime: number;
  endTime: number;
  portOrganisationId: string;
}

export interface VoyageActions {
  actions: DataBaseAction[];
}

export interface OwnershipOperationProtection {
  ownerId: string;
  technicalOperatorId: string;
  commercialOperatorId: string;
  builderId: string;
  managerId: string;
  protectionIndemnityId: string;
}

export interface DryDock {
  nextDryDock: string;
  lastDryDock: string;
}

export interface DimensionsCapacities {
  grt: number;
  nrt: number;
  capacity: number;
  draft: number;
  deadWeight: number;
  loa: number;
  beam: number;
  lightShip: number;
  numberOfTanks: number;
}

export interface CharterPartySpeed {
  economicalLadenSpeed: number,
  maximumBallastSpeed: number
}

export interface manual {
  manualType: string,
  name: string,
  fileId: string,
}

export interface VesselSpec {
  vesselType: string;
  yearBuilt: number;
  email: string;
  photoIds: string[];
  shipParticularId: string;
  charterPartySpeed: CharterPartySpeed;
  dimensionsCapacities: DimensionsCapacities;
  ownershipOperationProtection?: OwnershipOperationProtection;
  dryDock?: DryDock;
  manuals?: manual[];

}

export interface VesselName {
  name: string;
  flag: string;
  from: number;
}

export interface ProductQuantity {
  product: string;
  grossWeight: number;
  density: number;
}

export interface Nor extends VoyageActions {
  timestamp: number;
  norType: string;
  portOrganisationId: string;
  fileId: string;
  actions: DataBaseAction[];
}

export interface Sof extends VoyageActions {
  sofType: string;
  portOrganisationId: string;
  entries: [string, number][];
  fileId: string;
  actions: DataBaseAction[];
}

export interface Comment {
  incidentTimestamp: number;
  content: string;
  author: string;
  authorCommentTimestamp: number;
  authorTimezone: string;
  fileId: string;
}

export interface MiscDocument {
  miscDocumentType: string;
  comment: string;
  fileId: string;
}

export interface Laycan {
  startTimestamp: number;
  endTimestamp: number;
  ETATimestamp: number;
  organisationId: string;
}

export interface NauticalMile {
  sourceOrganisationId: string;
  destinationOrganisationId: string;
  distance: number;
}

export enum BunkerType {
  HFO = 'HFO',
  VLSFO = 'VLSFO',
  ULSFO = 'ULSFO',
  MGO = 'MGO',
  LSMGO = 'LSMGO',
}

export interface LoadingPermission {
  fileId: string;
  vesselImo: string;
  vesselName: string;
  variation: number;
  letterNumber: string;
  jettyNumber: string;
  loadingRate: string;
  laytimeStartDate: number;
  laytimeEndDate: number;
  productNames: string;
  products: ProductQuantity[];
  shipperOrganisationId: string;
  consigneeOrganisationId: string;
  agentOrganisationId: string;
  surveyorOrganisationId: string;
  destinationOrganisationId: string;
  laycan: Laycan;
}

export interface GenericNoonReportValidationRule {
  rowNumber: number;
  columnNumber: number;
  errorMessage: string;
}

export interface VesselBunker {
  foConsumption: number;
  doConsumption: number;
  robFO: number;
  robDO: number;
  fW: number;
}

export interface NoonReport {
  vesselImo: string;
  vesselName: string;
  timestamp: number;
  timezoneOffset: number;
  location: string;
  geoCoordinate: GeoCoordinate;
  distanceSinceLastReport: number;
  distanceTotal: number;
  distanceToGo: number;
  voyageFrom: string;
  voyageTo: string;
  etaTime: string;
  etaDate: string;
  hoursAtSeaSinceLastReport: number;
  hoursAtSeaTotal: number;
  vesselSpeedSinceLastReport: number;
  vesselSpeedAverage: number;
  conditionLB: string;
  seaCondition: string;
  bunkerStatus: VesselBunker;
  comment: string;
}

export interface VoyageLength {
  voyage: Voyage;
  days: number;
}

export interface Demurrage {
  voyageId: string;
  vesselName: string;
  vesselImo: string;
  shipVoyageNo: string;
  portOrganisationId: string;
  portName: string;
  portTimeZone: string;
  dischargeNor: number;
  dischargeAllFast: number;
  dischargeHoseDisconnected: number;
  exceededLaytimeHours: number;
  demurrage: number;
}

export interface OffHireBunker {
  bunkerType: string;
  amount: number
}

export interface OffHireItem {
  locationId: string;
  time: string
}

export interface VesselOffHire {
  id: string;
  documentId: string;
  vesselImo: string;
  status: string;
  offHireType: string;
  from: OffHireItem;
  to: OffHireItem;
  bunker: OffHireBunker[];
  relatedOffHire: string;
  usd: number;
  isDeleted: boolean;
  actions: DataBaseAction[]
}

export interface HelloGuide {
  fileName: string;
  fileId: string;
  documentType?: string;
  fileType: string;
  actions: DataBaseAction[];
  isDeleted?: boolean;
}

@Injectable(
  {providedIn: 'root'}
)
export class VesselsService {

  static norCodes = ['D01011', 'D01096', 'L01011', 'L01096'];
  static allFastCodes = ['D01051', 'L01051'];
  static hoseDisconnectCodes = ['D02071', 'D02102', 'D02103', 'D02128', 'D02129', 'D02135', 'D02142', 'L02051',
    'L02078', 'L02079', 'L02091', 'L02092', 'L02102'];

  constructor(private _httpWrapper: HttpWrapper,
    private _keycloakService: KeycloakService,
    private _vesselsService: VesselServiceSpa,
    private _voyageService: VoyageServiceSpa,
  ) { }

  static calculateLaytime(voyage: Voyage, norSofType: string) {
    const sofs = voyage.sofs
      .filter(s => s.sofType === norSofType)
      .flatMap(s => s.entries);

    const nors = sofs
      .filter(s => VesselsService.norCodes.some(c => c === s.key))
      .map(c => c.timestamp)
      .sort((a, b) => a - b);

    const disconnects = sofs
      .filter(s => VesselsService.hoseDisconnectCodes.some(c => c === s.key))
      .map(c => c.timestamp)
      .sort((a, b) => b - a);

    if (nors.length > 0 && disconnects.length > 0) {
      const diff = moment(disconnects[0]).diff(moment(nors[0]), 'hours', true);
      return Math.round(diff * 1000) / 1000;
    } else {
      return '';
    }
  }

  static voyageLengthFromLoadingToDischarge(v: Voyage) {
    const loadings = v.nors.filter(n => n.norType === 'loading')
      .sort((a, b) => a.timestamp - b.timestamp);

    const discharges = v.nors.filter(n => n.norType === 'discharging')
      .sort((a, b) => b.timestamp - a.timestamp);

    if (loadings.length !== 0 && discharges.length !== 0) {
      return moment(discharges[0].timestamp).diff(moment(loadings[0].timestamp), 'days');
    } else if (loadings.length !== 0) {
      return `${moment().diff(moment(loadings[0].timestamp), 'days')} ->`;
    }
  }

  static calculateBerthTime(v: Voyage, sofType: string): number | null {
    const sofs = v.sofs
      .filter(s => s.sofType === sofType)
      .flatMap(s => s.entries);

    const nors = sofs
      .filter(s => VesselsService.norCodes.some(c => c === s.key))
      .map(c => c.timestamp)
      .sort((a, b) => a - b);

    const allFasts = sofs
      .filter(s => VesselsService.allFastCodes.some(c => c === s.key))
      .map(c => c.timestamp)
      .sort((a, b) => a - b);

    if (nors.length > 0 && allFasts.length > 0) {
      const diff = moment(allFasts[0]).diff(moment(nors[0]), 'hours', true);

      return Math.round(diff * 1000) / 1000;
    } else {
      return null;
    }
  }

  getVessels() {
    return from(this._vesselsService.getAllVessels({}).then(v => v.vessels));
  }

  getActiveVessels() {
    return from(this._vesselsService.getActiveVessels({}).then(o => o.vessels));
  }

  getVessel(imo: string): Observable<Versioned<Vessel>> {
    return from(this._vesselsService.getVesselByImo({ vesselImo: imo }));
  }

  getVesselTypes() {
    return this._httpWrapper.get<VesselType[]>('vessels/types');
  }

  add(data: Vessel) {
    return from(this._vesselsService.addVessel(data));
  }

  addLoadingPermission(data: LoadingPermission) {
    return this._httpWrapper.post('vessels/loading-permission', data);
  }

  addNauticalMile(data: NauticalMile) {
    return this._httpWrapper.post('distance/nauticalMile', data);
  }

  getNauticalMiles(): Observable<NauticalMile[]> {
    return this._httpWrapper.get('distance/nauticalMile');
  }

  upsertHelloGuide(data: HelloGuide) {
    return this._httpWrapper.post('vessels/hello', data);
  }

  getAllHelloHelp(): Observable<HelloGuide[]> {
    return this._httpWrapper.get<HelloGuide[]>('vessels/hello');
  }

  getAllLoadingPermission(): Observable<LoadingPermission[]> {
    return this._httpWrapper.get<LoadingPermission[]>('vessels/loading-permission');
  }

  updateVessel(data: Versioned<Vessel>): Observable<VesselResponse> {
    return from(this._vesselsService.updateVessel({
      data: data.data,
      expectedVersion: data.version
    }));
  }

  getVoyagesBetween(dateRange: DateRange) {
    return this._voyageService.getAllVoyagesBetween({
      startTime: dateRange.startDate.toString(), endTime: dateRange.endDate.toString() // should resolve
    });
  }

  getLastActiveVesselVoyages() {
    return from(this._voyageService.getLatestVoyages({}).then(v => v.voyages));
  }

  getALLVesselOffHire(imo: string) {
    return this._httpWrapper.get<VesselOffHire[]>(`vessels/off-hire/vessel/${imo}`);
  }

  addOffHire(offHire: VesselOffHire) {
    return this._httpWrapper.post('vessels/off-hire', offHire);
  }

  getOffHires() {
    return this._httpWrapper.get<VesselOffHire[]>('vessels/off-hire/all');
  }

  getOneOffHire(id: string) {
    return this._httpWrapper.get<VesselOffHire>(`vessels/off-hire/${id}`);
  }

  mapToVoyageLength(allVoyages: Voyage[]): VoyageLength[] {
    const sorted = allVoyages.filter(v => v.nors.length > 0)
      .sort((v1, v2) =>
        (v2.nors.sort((n1, n2) => n1.timestamp - n2.timestamp))[0].timestamp -
        (v1.nors.sort((n1, n2) => n1.timestamp - n2.timestamp))[0].timestamp);
    const lookups = _.groupBy(sorted, (item) => item.vesselName);
    for (const l of Object.keys(lookups)) {
      lookups[l] = lookups[l].sort((a, b) => a.shipVoyageNo.localeCompare(b.shipVoyageNo));
    }
    return sorted.flatMap(v1 => {
      const voyages = lookups[v1.vesselName];
      const index = voyages.indexOf(v1);

      if (index + 1 !== voyages.length) {
      }

      const nextVoyage = voyages[index + 1];

      const firstNorDate = v1.nors
        .filter(n => n.norType === 'loading')
        .sort((a, b) => b.timestamp - a.timestamp);

      const secondNorDate = nextVoyage.nors
        .filter(n => n.norType === 'loading')
        .sort((a, b) => b.timestamp - a.timestamp);

      if (!firstNorDate.length || !secondNorDate.length) {
        return [];
      }

      return [{
        voyage: v1,
        days: moment(secondNorDate[0].timestamp)
          .diff(moment(firstNorDate[0].timestamp), 'days'),
      }];
    });
  }

  getVoyagesBetweenWithLengths(dateRange: DateRange): Promise<VoyageLength[]> {
    return this._voyageService.getAllVoyagesBetween({
      startTime: dateRange.startDate.toString(), endTime: dateRange.endDate.toString()
    }).then(v => this.mapToVoyageLength(v.voyages));
  }

  getVoyagesByIMO(IMO: string) {
    return from(this._voyageService.getVoyagesByImo({ vesselImo: IMO}).then(v => v.voyages));
  }

  getVoyage(voyageId: string) {
    return from(this._voyageService.getVoyageById({ id: voyageId}));
  }

  getSOFDuration(startDate: number, endDate: number, sofType: string, startItem: string, endItem: string) {
    return this._httpWrapper.get<DurationSof[]>
    (`vessels/voyage/get-sof-duration/${startDate}/${endDate}/${sofType}/${startItem}/${endItem}`);
  }

  getSOFDurationExcel(startDate: number, endDate: number, sofType: string, startItem: string, endItem: string) {
    return this._httpWrapper.download(`vessels/voyage/get-sof-duration/excel/${startDate}/${endDate}/${sofType}/${startItem}/${endItem}`);
  }

  findPreviousVoyageId(voyageId: string) {
    return from(this._voyageService.getPreviousVoyageById({ id: voyageId }).then(o => o?.data?.id));
  }

  findNextVoyageId(voyageId: string) {
    return from(this._voyageService.getNextVoyageById({ id: voyageId }).then(o => o?.data?.id));
  }

  addVoyage(voyage: Voyage) {
    return from(this._voyageService.addVoyage(voyage).then(v => v.data));
  }

  updateVoyage(voyage: Voyage) {
    return from(this._voyageService.updateVoyage({data: voyage, expectedVersion: 0 }).then(v => v.data));
  }

  importNoonReport(date: string, reportType: string, fileList: FileList): Observable<GenericNoonReportValidationRule[]> {
    return this._httpWrapper.postMultiPart(`vessels/noon-report/${date}`, this.toFormData(reportType, fileList));
  }

  getNoonReportsBetween(dr: DateRange): Observable<NoonReport[]> {
    return this._httpWrapper.get<NoonReport[]>(`vessels/noon-reports/${dr.startDate.getTime()}/${dr.endDate.getTime()}`);
  }

  totalVoyageLoading(voyage: Voyage) {
    if (voyage.billOfLadings) {
      const result = voyage.billOfLadings.filter(bill => bill.revise !== true)
        .map(b => b.products.map(p => p.grossWeight).reduce((x, y) => x + y, 0))
        .reduce((m, n) => m + n, 0);
      return {
        vesselImo: voyage.vesselImo,
        total: Math.round(result * 1000) / 1000
      };
    }
  }

  portDischarge(voyage: Voyage) {
    if (voyage.billOfLadings.length > 0) {
      return voyage.billOfLadings.map(b => (
        {
          vesselImo: voyage.vesselImo,
          voyageNo: voyage.shipVoyageNo,
          loadingPortOrganisationId: b.loadingPortOrganisationId,
          dischargingPortOrganisationId: b.dischargingPortOrganisationId,
          total: Math.round(_.sumBy(b.products, i => i.grossWeight)),
        }
      ));
    }
  }

  findVessel(imo: string, vessels: Vessel[]) {
    const vessel = vessels.find(v => v.imo === imo);
    if (vessel && vessel.name) {
      return vessel.name.name;
    } else {
      return 'Vessel not found';
    }
  }

  reportVesselsSummary(data: any[]) {
    const grouped = _.groupBy(data, (item) => item.vesselImo);
    return _(grouped).map((items, key) => (
      {
        vesselImo: key,
        totalWeight: Math.round(_.sumBy(items, i => i.total)),
        count: _.size(items)
      }
    )).value();
  }

  reportPortSummary(data: any[]) {
    const grouped = _.groupBy(data, (item) => item.dischargingPortOrganisationId);
    return _(grouped).map((items, key) => (
      {
        dischargingPortOrganisationId: key,
        totalWeight: Math.round(_.sumBy(items, i => i.total)),
        count: _.size(items)
      }
    )).value();
  }

  reportCountrySummary(data: any[]) {
    const grouped = _.groupBy(data, (item) => item.country);
    return _(grouped).map((items, key) => ({
      country: key,
      totalWeight: Math.round(_.sumBy(items, i => i.dischargeWeight)),
      count: _.size(items)
    })).value();
  }

  reportPortUse(voyage: Voyage) {
    return voyage.sofs.map(s => {
      const portDues = [];
      const allFasts = s.entries.filter(e => e.key === 'L01051' || e.key === 'D01051')
        .sort((l1, l2) => l1.timestamp - l2.timestamp);
      const clearJetties = s.entries.filter(e => e.key === 'D03016' || e.key === 'L03016')
        .sort((l1, l2) => l1.timestamp - l2.timestamp);
      while (allFasts.length > 0 && clearJetties.length > 0) {
        portDues.push(moment(clearJetties.pop()?.timestamp).diff(moment(allFasts.pop()?.timestamp), 'hours', true));
      }
      const nort = s.entries.filter(e => e.key === 'L01011' || e.key === 'D01011')
        .sort((e1, e2) => e1.timestamp - e2.timestamp)[0];
      const hoseDisconnect = s.entries.filter(e => e.key === 'L02051' || e.key === 'D02051')
        .sort((e1, e2) => e2.timestamp - e1.timestamp)[0];
      const layTime = (nort !== undefined && hoseDisconnect !== undefined) ?
        Math.round((moment(hoseDisconnect.timestamp).diff(moment(nort.timestamp), 'hours', true)) * 100) / 100 : null;
      return {
        vesselName: voyage.vesselName,
        voyageNo: voyage.shipVoyageNo,
        vesselImo: voyage.vesselImo,
        sofType: s.sofType,
        portId: s.portOrganisationId,
        portDues: portDues.length > 0 ? Math.round(_.sumBy(portDues) * 100) / 100 : 'N/A',
        layTimePerPort: layTime ? layTime : 'N/A'
      };
    });
  }

  isVerified() {
    return true;
    // if (voyageActions.actions !== undefined) {
    //   return voyageActions.actions.sort((t1, t2) => t2.timestamp - t1.timestamp)[0].userAction === ActionType.Verify;
    // } else {
    //   return false;
    // }
  }

  getDemurrage(dateRange: DateRange, dailyRate: number, allowedLaytime: number) {
    return this._httpWrapper.get<Demurrage[]>(
      `voyages/demurrage/${dateRange.startDate.getTime()}/${dateRange.endDate.getTime()}/${dailyRate}/${allowedLaytime}`);
  }

  downloadDemurrageExcel(dateRange: DateRange, dailyRate: number, allowedLaytime: number) {
    return this._httpWrapper.download(
      `voyages/demurrage/download/${dateRange.startDate.getTime()}/${dateRange.endDate.getTime()}/${dailyRate}/${allowedLaytime}`);
  }

  downloadReportTemplate(imo: string) {
    return this._httpWrapper.download(`vessels/noon-reports/template/${imo}`);
  }

  downloadVoyageActivityExcel(startTimestamp: number, endTimestamp: number) {
    return this._httpWrapper.download(`vessels/voyages/excel/${startTimestamp}/${endTimestamp}`);
  }

  countPorts(v: Voyage, sofType: string) {
    if (v.sofs && v.sofs.filter(n => n.sofType === sofType).length > 0) {
      return _.chain(v.sofs)
        .filter(n => n.sofType === sofType)
        .map(n => n.portOrganisationId)
        .groupBy(n => n)
        .map((items, key) => key)
        .value().length;
    } else {
      return '';
    }
  }

  verify(item: VoyageActions, partName: string) {
    if (confirm(`Are you sure you want to verify this ${partName}? Once verified this action cannot be undone.`)) {
      if (item.actions === undefined) {
        item.actions = [];
      }
      const action: DataBaseAction = {
        userName: this._keycloakService.getUser().username,
        timestamp: new Date().getTime(),
        userAction: ActionType.Verify
      };
      item.actions.push(action);
      return true;
    }
    return false;
  }

  removeVerification(item: VoyageActions, partName: string) {
    if (confirm(`Are you sure you want to remove this ${partName} verification?`)) {
      const action: DataBaseAction = {
        userName: this._keycloakService.getUser().username,
        timestamp: new Date().getTime(),
        userAction: ActionType.AdminRevert
      };
      item.actions.push(action);
      return true;
    }
    return false;
  }

  private toFormData(docType: string, fileList: FileList) {
    const formData = new FormData();
    const file = fileList.item(0);
    if (!file) {
      throw new Error('No file selected');
    }
    formData.append('report-type', docType);
    formData.append('file', file);
    return formData;
  }

  calculateVesselAge(v: Vessel) {
    return moment().diff(moment(new Date(v.spec!.yearBuilt ?? '', 1, 1)), 'years');
  }

}
